export interface Price {
    normal: number;
    extra?: {
        value: number,
        period: string
    }[];
}

export interface CrypDoc { // for client side!
    _id: string;
    _rev: string;
    ckey: {};
    fullName?: string;
    info?: {
        company_name: string,
        nip: string,
        phone: string,
        email: string,
        address: string,
        regon: string,
        krs: string
    };
    appColors?: {
        background: {
          color1: string;
          color2: string;
        };
        primaryColor: string;
        secondaryColor: string;
        borderColor?: string;
        buttonTextColor1?: string;
        buttonTextColor2?: string;
      };
    priorityFieldHidden?: boolean;
    contUnassignedOrdersNotification?: boolean;  // flag enabling continuous notification if any unassigned order exists
}
export interface Colors {
    background?: {
      color1: string;
      color2: string;
    };
    primaryColor: string;
    secondaryColor: string;
    borderColor?: string;
    buttonTextColor1?: string;
    buttonTextColor2?: string;
  }
export class MenuItem {
    type?: string = null; // for data base type definition
    _id: string = null;
    _rev?: string = null;
    _rev_tree?: any;
    lang?: string = null;
    name: string = null;
    desc?: string = null; // description
    ingr?: string[] = null; // ingridients
    weight?: string = null; // should be 'size'...
    private priceObj: Price = { normal: 0 }; // different currencies? proposition, objecti with keys: PLN,USD,EUR etc... (_price is like special document member for couchdb [underscore at the beginning])
    time?: number = null; // approximate[minutes]
    alerg?: string[] = null; // alergens
    variant?: string[] = null; // variant of the item eg. wege/vegan rare/mediam/well... what about change in alergen list?...
    other?: any = null; // extra stuff
    adultOnly?: boolean = false; // eg. includes alcohol
    hidden?: boolean = false;
    accepted?: boolean = null;
    imageFilename?: string = null;

    public set price(p) {

        if (typeof (p) === 'number') {
            this.priceObj.normal = p;
            return
        }

        if (typeof (p) === 'string') {
            const pn = Number(p)
            if (!isNaN(pn)) {
                this.priceObj.normal = pn;
            } else {
                this.priceObj = JSON.parse(p);
            }
            return
        }

        if ((p as Price).normal) {
            this.priceObj = p
        }
    }

    public get price() {
        const now = new Date();
        if (!('extra' in this.priceObj)) {
            return this.priceObj.normal
        }
        for (let e of this.priceObj.extra) {
            let [days, hours] = e.period.split(':');
            let [dstart, dend] = days.split('-').map((d) => Number(d));
            let [hstart, hend] = hours.split('-').map((h) => Number(h));
            if (dstart <= now.getDay() && now.getDay() <= dend &&
                hstart <= now.getHours() && now.getHours() < hend) {
                return e.value
            }
        }
        return this.priceObj.normal
    }

    /**
     * for menu item editor, parse object to readable string to view in form
     */
    public get price_str() {
        // for menu item editor
        return JSON.stringify(this.priceObj)
    }

    constructor(v?: MenuItem) { //{ ingr?: string, alerg?: string, variant?: string }) {
        // in case where from the form ingridients, and alergens are comma separated
        if (v) {
            const temp = v as any;
            for (const p of ['ingr', 'alerg', 'variant']) {
                if (!temp[p]) { temp[p] = null; } // for undefined
                temp[p] = p in v && Array.isArray(v[p]) || v[p] === null ? v[p] : v[p].split(',').map(i => i.trim());
            }
            // temp.priceObj = new Price(v.price_obj)
            Object.assign(this, temp);
        }
    }

    clearEmpty(): void {
        for (const p in this) {
            if (this[p] === null) { delete this[p]; }
        }
        if (this._rev_tree) { delete this._rev_tree };
    }
}

export interface MenuSection {
    id?: number;
    name: string;
    hidden?: boolean;
    items: MenuItem[];
}

export class Menu {
    type?: string = null; // for data base type definition
    _id: string = null;
    _rev?: string = null;
    menuId: string = null;
    name = 'Menu';
    ckey?: JsonWebKey;
    propsToView?: string[] = ['name', 'price'];
    lang: string = null;
    sections: MenuSection[] = [{ id: 0, name: 'default', items: [] }];
    onlineOrderMode: OrderMode = OrderMode.only_order_no_payment;
    callServiceBtnEnabled: boolean = false;
    restName?: string = '';
    appVer?: string = '';
    allowEmployeEdit?: boolean = false;
    accepted?: boolean = null;
    openHours?: RestaurantOpenHours;
    tablesInfo?: RestaurantTables;

    constructor(m?: { sections: { name: string, items: MenuItem[] }[] }) {
        if (m) {
            const temp = JSON.parse(JSON.stringify(m)) as any;
            for (const sIdx in m.sections) {
                if (temp.sections[sIdx]?.id == undefined) { temp.sections[sIdx].id = sIdx; };
                for (let iIdx in m.sections[sIdx].items) {
                    temp.sections[sIdx].items[iIdx] = new MenuItem(m.sections[sIdx].items[iIdx]);
                };
            };
            Object.assign(this, temp);
        };
    };
};

export enum OrderMode {
    no_online_orders,
    only_order_no_payment,
    only_order_with_payment,
    order_with_or_without_payment
}

export class OrderItem {
    ref: string = null; // menuItem Id
    name: string = null;
    quant = 0;
    variant?: { name: string, idx: number } = null;
    price = 0;
    priority?: number = 0; // higher -> deliver faster
    adultOnly: boolean = false;
    size?: string = null;

    constructor(i: MenuItem, variant?: { name: string, idx: number }) {
        this.ref = i._id;
        this.name = i.name;
        this.price = i.price;
        this.quant = 1;
        this.variant = variant ? variant : null;
        this.priority = 0;
        this.adultOnly = i.adultOnly;
        this.size = i.weight ? i.weight : null;
    }
}


export class Order {
    _id: string = null;
    _rev?: string = null;
    revRemote?: string = null;
    items: OrderItem[] = [];
    status: OrderStatus = OrderStatus.draft;
    lang?: string;
    madeBy?: string;
    servedBy: string = '';
    msgToRest?: MessageClientRest[] = [];
    msgFromRest?: MessageClientRest[] = [];
    symKey?: { key: string, iv: string } = { key: '', iv: '' };
    tableId?: string;
    restId?: string;
    restName?: string;
    paid: PaymentStatus = PaymentStatus.NONE;
    paymentDetails?: Payment;
    comment?: string;
    menuRev?: string;
    pushSubscription?: {};
    pickUpTime?: string;

    get createdTimeStamp(): number {
        return Number(this._id.split(':')[2])
    }

    get createdDate(): Date {
        return new Date(Number(this._id.split(':')[2]))
    }

    get served(): boolean {
        return !(
            !('servedBy' in this) ||
            this.servedBy === null ||
            this.servedBy.length === 0)
    }
    value(): number {
        return this.items.reduce((prev, curr, idx, arr) => prev + curr.price * curr.quant, 0);
    }

    itemsQuantity(): number {
        return this.items.reduce((prev, curr) => prev + curr.quant, 0);
    }

    allMsgs(): MessageClientRest[] {
        this.msgToRest.forEach(m => m['dir'] = 'to')
        this.msgFromRest.forEach(m => m['dir'] = 'from')
        return this.msgToRest.concat(this.msgFromRest).sort((a, b) => b.time - a.time)
    }

    incomingImportantMsg(): boolean {
        return this.msgToRest.some(msg => {
            return [MsgTypes.change_order_req, MsgTypes.call_service_req, MsgTypes.change_pickup_req].includes(msg.type);
        })
    }


    // @returns  pair of colors, for background and for font calculated to be visible 
    verificationColor(): string[] {
        if (this._id !== null) {
            const enc = Buffer.from(this._id.split(':').splice(3).join('')).toString('hex');
            const idx = parseInt(enc, 16) % verificationColors.length;
            const backgroundColor = verificationColors[idx]
            const r = parseInt(backgroundColor.slice(1, 3), 16) / 255;
            const g = parseInt(backgroundColor.slice(3, 5), 16) / 255;
            const b = parseInt(backgroundColor.slice(5, 7), 16) / 255;
            const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b);
            const fontColor = luminance > 0.3 ? '#000000' : '#ffffff';
            return [backgroundColor, fontColor];
        }
        return ['#000000', '#000000'];
    }

    adultOnly(): boolean {
        return this.items.map(i => i.adultOnly).includes(true);
    }

    constructor(obj: any = {}) {
        for (let p in obj) {
            if (p !== 'msgToRest' && p !== 'msgFromRest') {
                this[p] = obj[p];
            } else {
                for (let m of obj[p]) {
                    this[p].push(new MessageClientRest(m));
                }
            }

        }
    }

}

export class User {
    login: string = null;
    name?: string;
    email?: string;
    guest?: { login: string, JWT?: string };

    // constructor(login?: string, name?: string, email?: string, guest?: { login: string, JWT?: string }) {
    constructor({ login = null, name = null, email = null, guest = null } = {}) {
        if (login) { this.login = login; };
        if (name) { this.name = name; };
        if (email) { this.email = email; };
        if (guest) { this.guest = guest; } else { this.guest = { login: null } };

        // if (guest && guest.login) { this.guest = { login: guest.login }; };
        // if (guest && guest.JWT) { this.guest.JWT = guest.JWT; };
    }
}

export class Restaurant {
    id: string = null;
    name?: string = null;
    info?: any = null; // for 'now' before all necessary legal info precised
}

export enum OrderStatus {
    draft,
    sent,
    accepted,
    rejected,
    ready,
    delivered,
}

export interface Lang {
    code: string;
    name: string;
}

// https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes#DE
// https://github.com/angular/angular/tree/master/packages/common/locales
export const allLang: Lang[] = [
    { code: 'pl', name: 'polski' },
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'uk', name: 'українська мова' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'lengua española' },
    { code: 'it', name: 'lingua italiana' },
    { code: 'cs', name: 'čeština' },
    { code: 'sk', name: 'slovenský' },
    { code: 'lv', name: 'latviešu valoda' },
    { code: 'lt', name: 'lietuvių kalba' },
    { code: 'et', name: 'eesti keel' },
    { code: 'nl', name: 'de Nederlanders' },
    { code: 'pt', name: 'idioma portugues' },
    { code: 'ga', name: 'Gaeilge' },
    { code: 'tr', name: 'Türkçe' },
    { code: 'no', name: 'norsk språk' },
    { code: 'sv', name: 'svenska' },
    { code: 'fi', name: 'Suomen kieli' },
    { code: 'ka', name: 'ქართული ენა' },
    { code: 'zh', name: '中文' }
]

export const langsWithFullTranslation: Lang[] = [
    { code: 'pl', name: 'polski' },
    { code: 'en', name: 'English' }
]

export enum MsgTypes {
    change_order_req,
    change_table_req, // deprecated -> change_pickup_req
    order,
    change_order_accepted,
    change_order_rejected,
    change_table_accepted, // deprecated -> change_pickup_accepted
    change_table_rejected, // deprecated -> change_pickup_rejected
    call_service_req,
    call_service_accepted,
    call_service_rejected,
    verification_req,
    change_pickup_req,
    change_pickup_accepted,
    change_pickup_rejected,
    change_table_by_rest
}

export const verificationColors: string[] = [
    '#c0c0c0',//silver
    '#ffffff',//white
    '#ff0000',//red
    '#ff00ff',//magenta
    '#00ff00',//lime
    '#ffff00',//yellow
    '#0000ff',//blue
    '#000080',//navy
    '#00ffff',//cyan
    '#ffa500',//orange
]
export class MessageClientRest {
    type: MsgTypes;
    content: any;
    time?: number = Date.now();
    dir?: 'to' | 'from'

    constructor(obj: any = {}) {
        for (let p in obj) {
            this[p] = obj[p];
        }
    }

    ordDiffToTxt(): string {
        if (this.content) {
            const cnt = JSON.parse(this.content).ordDiff
            const items_changes = cnt.filter(i => 'ref' in i && (i.quant !== 0 || i.priority !== 0))
                .map(i => `${i.name}${i.variant ? ` (${i.variant.name})` : ''}: ${i.quant > 0 ? `+${i.quant}` : i.quant < 0 ? i.quant : ''}${i.priority > 0 ? $localize`${i.quant ? `, ` : ''}podaj na początku` : i.priority < 0 ? $localize`${i.quant ? `, ` : ''}nie podawaj na początku` : ''}`).join('; ');
            const new_comm = cnt.filter(i => 'new_comment' in i).map(i => i.new_comment).join('');
            return `${items_changes ? items_changes : ''}${items_changes && new_comm ? '; ' : ''}${new_comm ? $localize` nowy komentarz: ${new_comm}` : ''}`;
        } else {
            return 'no content'
        }
    }
    print() {
        let msg = $localize`nieznany typ wiadomości: ${this.type}`;
        switch (this.type) {
            case MsgTypes.change_order_req:
                msg = this.ordDiffToTxt();
                break;
            // case MsgTypes.change_table_req:
            //     msg = $localize`Prośba o zmianę miejsca odbioru ${this.content.split(':')[0]} -> ${this.content.split(':')[1]}`
            //     break;
            case MsgTypes.order:
                // accepted, unassigned, rejected, ready, new_order::ORD_ID
                const ordMsg = this.content.split('::');
                switch (ordMsg[0]) {
                    case 'accepted':
                        msg = $localize`Zamówienie przyjęte do realizacji.`;
                        break;
                    case 'unassigned':
                        msg = $localize`Realizacja zamówienia wstrzymana.`;
                        break;
                    case 'rejected':
                        msg = $localize`Zamówienie odrzucone.`;
                        break;
                    case 'new_order':
                        msg = $localize`Nowe zamówienie przy stoliku: ${ordMsg[1].split(':')[3]}.`
                        break;
                    case 'ready':
                        msg = $localize`Zamówienie gotowe!`
                        break;
                    case 'ready_and_pickup':
                        msg = $localize`Proszę podejść po odbiór zamówienia.`
                        break;
                }
                break;
            case MsgTypes.change_order_accepted:
                msg = $localize`Zmiana zamówienia zaakceptowana. (${this.ordDiffToTxt()})`
                break;
            case MsgTypes.change_order_rejected:
                msg = $localize`Zmiana zamówienia odrzucona.`
                break;
            // case MsgTypes.change_table_accepted:
            //     msg = $localize`Zmiana miejsca odbioru zaakceptowana, nowe miejsce odbioru: ${this.content.split(':')[1]}.`;
            //     break;
            // case MsgTypes.change_table_rejected:
            //     msg = $localize`Zmiana miejsca dobioru odrzucona.`;
            //     break;
            case MsgTypes.call_service_req:
                msg = $localize`Stolik prosi o podejście obsługi.`;
                break;
            case MsgTypes.call_service_accepted:
                msg = $localize`Obsługa w drodze do miejsca odbioru.`;
                break;
            case MsgTypes.call_service_rejected:
                msg = $localize`Prośba o podejście obsługi odrzucona.`;
                break;
            case MsgTypes.verification_req:
                msg = $localize`Prośba o weryfikację zamówienia`;
                break;
            case MsgTypes.change_pickup_req:
                const [sTid,sTn,sP,tTid,tTn,tP] = this.content.split('\|');
                if (sTid === tTid) {
                    // same table
                    msg = $localize `Prośba o zmianę godziny odbioru: ${sP} -> ${tP}`;
                };
                if (sP === tP) {
                    // same pickup time
                    msg = $localize `Prośba o zmianę miejsca odbioru: ${sTn} -> ${tTn}`;
                };
                if (sTid !== tTid && sP !== tP) {
                    // different table and pickup time
                    msg = $localize `Prośba o zmianę miejsca: ${sTn} -> ${tTn}, oraz godziny odbioru: ${sP} -> ${tP}`;
                }
                break;
            case MsgTypes.change_pickup_accepted:
                msg = $localize `Zmiana sposobu odbioru zaakceptowana`;
                break
            case MsgTypes.change_pickup_rejected:
                msg = $localize `Zmiena sposobu odbioru odrzucona`;
                break
            case MsgTypes.change_table_by_rest:
                const [sT,tT] = this.content.split('\|')
                msg = $localize `Zmiana miejsca odbioru: ${sT} -> ${tT}`
                break
        }
        return msg;
    }
}

export enum PaymentStatus {
    NONE, // not paid online
    PENDING,
    SUCCESS,
    FAILURE
}

export interface Payment {
    _id: string,
    rev: string,
    _deleted?: boolean,
    paymentStatus: PaymentStatus,
    orderID?: string,
    remoteID: string,
    amount: number,
    paymentDate: string, // TODO: change to datetime

}

/**
 * interface for construction report object from couchdb "raw" data, generated by cloud function from 'orders-log' db
 */
export interface RawReport {
    _id: string;
    _rev: string;
    creationDate: number;
    date: number;
    type: string;
    orders: {
        ord: {
            _id: string,
            status: OrderStatus,
            paid: PaymentStatus,
            paymentDetails?: {
                paymentStatus: PaymentStatus,
                amount: string,
                paymentDate?: string,
                remoteId?: string
            },
            items: {
                ref: string,
                name: string,
                quant: number,
                variant?: {
                    name: string,
                    idx: number
                },
                price: number,
                priority?: number,
                adultOnly: boolean,
                size?: string
            }[],
            servedBy?: string,
            msgToRest?: {
                time: number,
                type: MsgTypes,
                content?: string
            }[],
            msgFromRest?: {
                time: number,
                type: MsgTypes,
                content?: string
            }[]
        }
    }[]
}

export interface ReportShortRow {
    id: string,
    creationTime: Date,
    status: OrderStatus,
    paid: PaymentStatus,
    value: number
}
export class OrdersReport {
    _id: string = null;
    date: Date;
    creationDate: Date;
    value: {
        sent: number,
        accepted: number,
        rejected: number,
        ready: number,
        paidOnline: number,
    } = { sent: 0, accepted: 0, rejected: 0, ready: 0, paidOnline: 0 };


    orders: {
        _id: string,
        status: OrderStatus,
        paid: PaymentStatus,
        paymentStatus?: PaymentStatus,
        amount?: number, // from paymentDetails
        paymentDate?: string,
        remoteID?: string,
        items: {
            name: string,
            quant: number,
            price: number,
            size?: string,
            variant?: string
        }[],
        servedBy?: string,
        msgToRest?: {
            time: number,
            type: MsgTypes,
            content?: string
        }[],
        msgFromRest?: {
            time: number,
            type: MsgTypes,
            content?: string
        }[]
    }[] = []



    constructor(rawReport: RawReport) {

        this._id = rawReport._id;
        this.date = new Date(rawReport.date);
        this.creationDate = new Date(rawReport.creationDate);
        const rep = rawReport.orders
        for (let r of rep) {
            let order = {
                _id: r.ord._id,
                status: r.ord.status,
                paid: r.ord.paid,
                items: [],
                msgToRest: [],
                msgFromRest: []
            };
            for (let i of r.ord.items) {
                order.items.push({
                    name: i.name,
                    quant: i.quant,
                    price: i.price,
                    size: i.size,
                    variant: i.variant !== null && 'name' in i.variant ? i.variant.name : null,
                });
            };

            if ('paymentDetails' in r.ord) {
                order['paymentStatus'] = r.ord.paymentDetails.paymentStatus;
                order['paymentDate'] = r.ord.paymentDetails.paymentDate;
                order['remoteID'] = r.ord.paymentDetails.remoteId;
                order['amount'] = Number(r.ord.paymentDetails.amount);
            } else {
                order['amount'] = (new Order(r.ord)).value();
            };
            if ('servedBy' in r.ord) { order['servedBy'] = r.ord.servedBy };
            if ('msgFromRest' in r.ord) {
                for (let mfr of r.ord.msgFromRest) {
                    order.msgFromRest.push({
                        time: mfr.time,
                        type: mfr.type,
                        content: 'content' in mfr ? mfr.content : null
                    })
                }
            }
            if ('msgToRest' in r.ord) {
                for (let mtr of r.ord.msgFromRest) {
                    order.msgFromRest.push({
                        time: mtr.time,
                        type: mtr.type,
                        content: 'content' in mtr ? mtr.content : null
                    })
                }
            }

            this.orders.push(order)
        }

        this.value.accepted = this.calcValueAll(OrderStatus.accepted);
        this.value.rejected = this.calcValueAll(OrderStatus.rejected);
        this.value.sent = this.calcValueAll(OrderStatus.sent);
        this.value.ready = this.calcValueAll(OrderStatus.ready);
        this.value.paidOnline = this.calcValuePaidOnline();
        // if (date) {
        //     this.date = new Date(date);
        // } else {
        //     this.date = this.orders.length > 0 ? new Date(Number(this.orders[0]._id.split(':')[2])) : null;
        // }

    }

    calcValueAll(status: OrderStatus | null = null): number {
        return this.orders.filter((o) => {
            return status === null ? true : o.status === status;
        })
            .reduce((p, c) => {
                return p + this.orderValue(c);
            }, 0)
    }

    orderValue(o: { items: { quant: number, price: number }[] }): number {
        return o.items.reduce((p, c) => {
            return p + c.quant * c.price;
        }, 0);
    }
    calcValuePaidOnline(): number {
        return this.orders.filter((o) => {
            return o.paid === PaymentStatus.SUCCESS;
        })
            .reduce((p, c) => {
                return p + c.amount
            }, 0)
    }

    printSummary(): string {
        return $localize`Ilość zamówień: ${this.orders.length}, wartość gotowych: ${this.value.ready}, wartość opłaconych online: ${this.value.paidOnline}`
    }

    rows(): ReportShortRow[] {

        return this.orders.map((o) => {
            let creationTime = new Date(Number(o._id.split(':')[2]));
            return {
                id: o._id,
                creationTime,
                status: o.status,
                paid: o.paid,
                value: this.orderValue(o)
            }
        }).sort((a, b) => a.creationTime.getTime() - b.creationTime.getTime());
    }
}



export class Time {
    hours: number;
    minutes: number;
    constructor(hours: number, minutes: number) {
        this.hours = hours;
        this.minutes = minutes;
    };
    toString(): string {
        return `${this.hours.toLocaleString([], { minimumIntegerDigits: 2 })}\:${this.minutes.toLocaleString([], { minimumIntegerDigits: 2 })}`;
    };
    add(t: Time) {
        const m = this.minutes + t.minutes;
        return new Time(this.hours + t.hours+ Math.floor(m/60), m % 60);
    };
};


export class TableInfo {
    tableName: string = '';
    tableId: string = '';
}
export interface RestWorkTimeAndTablesInfo {
    tables: TableInfo[];
    openTime: Time;
    closeTime: Time;
    minimumWaitingTime: Time;
    prevPickUpTime?: string;
    prevTableId?: string;
    disableClose: boolean;
}

/**
 * close time = when restaurant do not want new orders (not when it really closes)
 */
export class DayOpenHours {
    openTime: Time;
    closeTime: Time;

    constructor(oT:Time,cT:Time) {
        this.openTime = new Time(oT.hours,oT.minutes);
        this.closeTime = new Time(cT.hours,cT.minutes);
    }
}

/**
 * minimumWaitingTime - for orders with pick up, the client can't set pickUpTime earlier then now+minimumWaitingTime
 */
export class RestaurantOpenHours {
    minimumWaitingTime: Time;
    // tables: TableInfo[];
    openHours: {
        0:DayOpenHours, // sunday
        1:DayOpenHours, 
        2:DayOpenHours,
        3:DayOpenHours,
        4:DayOpenHours,
        5:DayOpenHours,
        6:DayOpenHours // saturday
    };

    constructor(prev?: RestaurantOpenHours) {
        if (!prev) {
            this.minimumWaitingTime = new Time(0, 0);
            // this.tables = [];
            this.openHours = [...Array(7).keys()].reduce((o, key) => ({ ...o, [key]: new DayOpenHours(new Time(0, 0),new Time(23, 59)) }), {}) as any;  //{ openTime: new Time(0, 0), closeTime: new Time(23, 59) }
        } else {
            Object.assign(this,prev);
        };
    }

    // addTable(table:TableInfo) {
    //     this.tables.push(table);
    // };

    // removeTable(table:TableInfo) {
    //     //TODO
    // }

    changeOpenHours(day:number,hours:DayOpenHours) {
        this.openHours[day] = hours;
    }
}

export const weekDays = [
    $localize `Niedziela`,
    $localize `Poniedziałek`,
    $localize `Wtorek`,
    $localize `Środa`,
    $localize `Czwartek`,
    $localize `Piątek`,
    $localize `Sobota`]

export class RestaurantTables {
    _id?:string;
    _rev?:string;
    lang: string = '';
    tables: TableInfo[] = [];

    constructor(rt?:RestaurantTables) {
        if (rt) {
            Object.assign(this,rt);
        };
    }
    addTable(table:TableInfo) {
        this.tables.push(table);
    }

    removeTable(tableId:string) {
        this.tables = this.tables.filter(t => t.tableId !== tableId);
    }
}